import { Icon } from "@iconify/react";
import { Colors } from "../../utils/colors";
import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { RWebShare } from "react-web-share";
import { useTheme } from "../../hooks/useTheme";
import AlertButtonWrapper from "../Containers/AlertButtonWrapper";
import { NotificationType } from "@markit/common.types";
import { showNotificationBanner } from "../../utils/notificationUtils";
import { onShareLink } from "../../utils/shareLink";
import { isDesktop } from "react-device-detect";
import ShareQRCodeModal from "../DisplayModal/ShareQRCodeModal";

export type ShareLinkOptionsProps = {
  link: string;
};

// Standard share link modal to be used for any place where we want to show additional share options
const ShareLinkOptions = (props: ShareLinkOptionsProps) => {
  const { link } = props;
  const dispatch = useDispatch();
  const { theme } = useTheme();

  const [alertText, setAlertText] = useState("");
  const [qrCodeModal, setQrCodeModal] = useState(false);

  const styles = {
    smallText: { fontSize: 11, paddingTop: 7 },
  };

  const onCopyClick = useCallback(() => {
    navigator.clipboard
      .writeText(link)
      .then(() => {
        showNotificationBanner(
          dispatch,
          "Copied!",
          NotificationType.AFFIRMATIVE
        );
      })
      .catch((err) => {
        alert(err);
      });
  }, [dispatch, link]);

  const shareUrlLink = useCallback(async () => {
    await onShareLink("Share your link!", link);
  }, [link]);

  const onMessagesClick = useCallback(() => {
    const url = `sms:&body=${encodeURIComponent(link)}`;
    window.location.href = url;
  }, [link]);

  const renderShareTo = useMemo(
    () => (
      <div
        className="ShareEventModalLinkIcon"
        style={theme ? theme.SecondaryBG : { backgroundColor: Colors.WHITE }}
      >
        <Icon icon="mdi:share-outline" height={26} />
      </div>
    ),
    [theme]
  );

  return (
    <>
      <AlertButtonWrapper
        buttonComp={
          <div className="ShareEventModalLinks" style={theme?.PrimaryText}>
            <div>
              <div
                className="ShareEventModalLinkIcon"
                style={
                  theme ? theme.SecondaryBG : { backgroundColor: Colors.WHITE }
                }
                onClick={onCopyClick}
              >
                <Icon icon="ep:link" height={26} />
              </div>
              <div style={styles.smallText}>Copy Link</div>
            </div>
            {isDesktop ? (
              <RWebShare data={{ url: link }}>
                <div className="ColumnNormal">
                  {renderShareTo}
                  <span style={styles.smallText}>Share to...</span>
                </div>
              </RWebShare>
            ) : (
              <div>
                <div onClick={shareUrlLink}>{renderShareTo}</div>
                <span style={styles.smallText}>Share to...</span>
              </div>
            )}
            <div>
              <div
                className="ShareEventModalLinkIcon"
                style={
                  theme ? theme.SecondaryBG : { backgroundColor: Colors.WHITE }
                }
                onClick={onMessagesClick}
              >
                <Icon icon="ion:chatbubble-outline" height={22} />
              </div>
              <div style={styles.smallText}>Messages</div>
            </div>
            <div>
              <div
                className="ShareEventModalLinkIcon"
                style={
                  theme ? theme.SecondaryBG : { backgroundColor: Colors.WHITE }
                }
                onClick={() => setAlertText("Coming soon...")}
              >
                <Icon icon="mdi:instagram" height={24} />
              </div>
              <div style={styles.smallText}>Instagram</div>
            </div>
            <div>
              <div
                className="ShareEventModalLinkIcon"
                style={
                  theme ? theme.SecondaryBG : { backgroundColor: Colors.WHITE }
                }
                onClick={() => setQrCodeModal(true)}
              >
                <Icon icon="ion:qr-code" height={22} />
              </div>
              <div style={{ ...styles.smallText, textAlign: "center" }}>
                QR Code
              </div>
            </div>
          </div>
        }
        alertTextHeader={alertText}
        clearAlert={() => setAlertText("")}
      />
      {qrCodeModal ? (
        <ShareQRCodeModal
          setIsVisible={setQrCodeModal}
          shareUrl={link}
          downloadLabel={"link"}
        />
      ) : null}
    </>
  );
};

export default ShareLinkOptions;
